/**
 * Latency model for the simulation v2 enrichment layer (spec §5).
 *
 * Provides p50/p95/p99 estimates per actor + per-CF override map.
 * Numbers reflect Firestore-functions p95 from the Atlas runbook baseline.
 */
import type { LatencyBand } from './simulator.js';

export const LATENCY_DEFAULTS: Record<string, LatencyBand> = {
  user: { p50: 0, p95: 0, p99: 0 },
  'event-bus': { p50: 5, p95: 20, p99: 50 },
  cf: { p50: 80, p95: 200, p99: 500 },
  'cf-callable': { p50: 120, p95: 350, p99: 800 },
};

/**
 * Per-CF overrides for CFs with known heavier workloads.
 * Key = CF name as it appears in the manifest.
 */
const CF_OVERRIDES: Record<string, LatencyBand> = {
  processTransactionCategories: { p50: 200, p95: 600, p99: 1200 },
  generateAgentInsights: { p50: 300, p95: 900, p99: 2000 },
  processReportSchedules: { p50: 150, p95: 500, p99: 1000 },
  syncPlaidTransactions: { p50: 400, p95: 1200, p99: 3000 },
  reconcileSubscriptionTier: { p50: 100, p95: 300, p99: 600 },
  drainEventWAL: { p50: 50, p95: 180, p99: 400 },
  evaluateQuotaExhaustion: { p50: 60, p95: 180, p99: 350 },
  triggerRetentionOffer: { p50: 90, p95: 250, p99: 500 },
  runMcpToolWrite: { p50: 200, p95: 700, p99: 1500 },
  resetBudgetPeriod: { p50: 80, p95: 220, p99: 450 },
  tripCircuitBreaker: { p50: 10, p95: 30, p99: 60 },
};

/**
 * Returns the latency band for a simulation step.
 * @param actor - step actor ('user', 'cf', 'event-bus', 'cf-callable')
 * @param cfName - CF name for per-CF override lookup (undefined for non-CF steps)
 */
export function estimateLatency(actor: string, cfName?: string): LatencyBand {
  if (cfName && CF_OVERRIDES[cfName]) return CF_OVERRIDES[cfName];
  return LATENCY_DEFAULTS[actor] ?? LATENCY_DEFAULTS['cf'];
}
